import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { IFakturowniaChainRow } from 'core/hooks';
import { InvoiceKindPill, InvoiceStatusPill, MoneyCell } from 'molecules';
import { PATHS } from 'router/config';

import * as S from './FakturowniaInvoices.styled';

interface IInvoiceChainSectionProps {
  /**
   * Advance invoices that draw against this estimate. Empty array
   * when the contract is unpaid; component hides itself only when
   * `advances` AND `finalInvoice` are both empty.
   */
  advances: IFakturowniaChainRow[];
  /** Closing invoice (may be null until the contract is fulfilled). */
  finalInvoice: IFakturowniaChainRow | null;
  /** Estimate's `priceGross` — used for the closure-vs-contract check. */
  estimateGross?: number | null;
  currency?: string | null;
}

const fmt = (n: number) => n.toFixed(2);

/**
 * Phase 12: the «Платежи по заказу» panel on an estimate's detail page.
 * Renders advances (chronologically) then the final invoice, footed by a
 * reconciliation row that calls out whether the chain matches the contract
 * amount. Mismatch ≠ bug-on-our-side — it's a signal that something in
 * Fakturownia is off and the financier should follow up by hand.
 */
export const InvoiceChainSection = memo(
  ({
    advances,
    finalInvoice,
    estimateGross,
    currency,
  }: IInvoiceChainSectionProps) => {
    const { t } = useTranslation();
    const history = useHistory();

    const sortedAdvances = useMemo(
      () =>
        [...advances].sort(
          (a, b) =>
            moment(a.issueDate || 0).valueOf() -
            moment(b.issueDate || 0).valueOf(),
        ),
      [advances],
    );

    const rows: IFakturowniaChainRow[] = useMemo(
      () => (finalInvoice ? [...sortedAdvances, finalInvoice] : sortedAdvances),
      [sortedAdvances, finalInvoice],
    );

    const paidSum = useMemo(
      () => rows.reduce((s, r) => s + (r.priceGross || 0), 0),
      [rows],
    );

    if (rows.length === 0) {
      return (
        <S.Card>
          <S.CardTitle>
            {t('pages.fakturownia.invoice.chainSection', { count: 0 })}
          </S.CardTitle>
          <div style={{ color: '#94a3b8', fontSize: 13 }}>
            {t('pages.fakturownia.invoice.chainEmptyHint')}
          </div>
        </S.Card>
      );
    }

    const estimateValue = estimateGross || 0;
    const diff = estimateValue - paidSum;
    const closed = Math.abs(diff) < 0.01 && estimateValue > 0;

    return (
      <S.Card>
        <S.CardTitle>
          {t('pages.fakturownia.invoice.chainSection', { count: rows.length })}
        </S.CardTitle>
        <S.Table>
          <thead>
            <tr>
              <th>{t('pages.fakturownia.invoices.columns.number')}</th>
              <th>{t('pages.fakturownia.invoices.columns.kind')}</th>
              <th>{t('pages.fakturownia.invoices.columns.date')}</th>
              <th>{t('pages.fakturownia.invoices.columns.status')}</th>
              <th style={{ textAlign: 'right' }}>
                {t('pages.fakturownia.invoices.columns.gross')}
              </th>
              <th style={{ textAlign: 'right' }}>
                {t('pages.fakturownia.invoice.paid')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                onClick={() =>
                  history.push(PATHS.financesInvoice.replace(':id', row.id))
                }
              >
                <S.NameCell>{row.number}</S.NameCell>
                <S.TagCell>
                  <InvoiceKindPill
                    kind={undefined}
                    isAdvance={row !== finalInvoice}
                    isFinal={row === finalInvoice}
                  />
                </S.TagCell>
                <td style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                  {row.issueDate
                    ? moment(row.issueDate).format('DD.MM.YYYY')
                    : '—'}
                </td>
                <S.TagCell>
                  <InvoiceStatusPill status={row.status} />
                </S.TagCell>
                <td>
                  <MoneyCell
                    amount={row.priceGross}
                    currency={row.currency || currency}
                  />
                </td>
                <td>
                  <MoneyCell
                    amount={row.paid}
                    currency={row.currency || currency}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </S.Table>

        <div
          style={{
            marginTop: 10,
            padding: '10px 12px',
            borderRadius: 8,
            background: closed ? '#f0fdf4' : '#fffbeb',
            border: `1px solid ${closed ? '#bbf7d0' : '#fde68a'}`,
            color: closed ? '#166534' : '#92400e',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {closed
            ? t('pages.fakturownia.invoice.chainSumOk', {
                sum: fmt(paidSum),
                currency: currency || '',
              })
            : t('pages.fakturownia.invoice.chainSumMismatch', {
                estimate: fmt(estimateValue),
                paid: fmt(paidSum),
                diff: fmt(diff),
                currency: currency || '',
              })}
        </div>
      </S.Card>
    );
  },
);
